import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { UserCircle, X } from "lucide-react";

const DISMISS_KEY = "dhc_incomplete_profile_dismissed";

export function IncompleteProfileBanner() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // Não verificar se já foi dispensado nesta sessão
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      // Detectar provedor de login (email, google, linkedin_oidc, etc.)
      const provider =
        (user.app_metadata as any)?.provider ||
        user.identities?.[0]?.provider ||
        "email";
      const isOAuth = provider !== "email";

      const { data: profile } = await supabase
        .from("profiles")
        .select("user_type, full_name, location, linkedin_url")
        .eq("id", user.id)
        .maybeSingle();

      if (cancelled || !profile || profile.user_type !== "candidate") return;

      // Para OAuth, checamos os campos extras do candidato
      let candidate: { headline: string | null; experience_level: string | null } | null = null;
      if (isOAuth) {
        const { data } = await supabase
          .from("candidates")
          .select("headline, experience_level")
          .eq("profile_id", user.id)
          .maybeSingle();
        candidate = data;
      }

      if (cancelled) return;

      // Critério mínimo (todos): nome e LinkedIn preenchidos
      const missingCore = !profile.full_name || !profile.linkedin_url;

      // Critério extra (apenas OAuth): localização e dados do candidato
      const missingOAuthExtras = isOAuth && (
        !profile.location ||
        !candidate?.headline ||
        !candidate?.experience_level
      );

      const isIncomplete = missingCore || missingOAuthExtras;

      if (isIncomplete) setShow(true);

    };

    check();
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;

  return (
    <Alert className="mb-6 border-primary/30 bg-primary/5">
      <UserCircle className="h-4 w-4 text-primary" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
        <div className="flex-1">
          <AlertTitle className="text-sm">Complete seu perfil</AlertTitle>
          <AlertDescription className="text-xs text-muted-foreground">
            Perfis completos têm 3x mais visualizações de recrutadores.
          </AlertDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => navigate("/candidate/dashboard?edit=1")}
          >
            Completar agora
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              sessionStorage.setItem(DISMISS_KEY, "1");
              setShow(false);
            }}
            aria-label="Dispensar"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Alert>
  );
}
